import { createSelector } from "@reduxjs/toolkit";
import { getDeeplyNestedValue } from "@multiconsult-group/multiplacer-core";
import { openapi } from "../../../openapi";
import { IRootState } from "../../app/store";
import { selectMmiPath, selectProjectNo } from "../../globalSelectors";
import {
  selectClickedMcBimItemId,
} from "../../ViewerRedux/viewerSlice";
import { IMcBimItem } from "@backend/src/shared/schemas/schemas";

const selectGetMcbimItemArgs = createSelector(
  [selectClickedMcBimItemId, selectProjectNo],
  (itemId, projectNo) => {
    if (!itemId || !projectNo) return null;
    return { projectNo, itemId: itemId.trim() };
  }
);

export const selectHttpGetMcbimItem = createSelector(
  [selectGetMcbimItemArgs, (state: IRootState) => state],
  (args, state) => {
    if (!args) return null;
    const { projectNo, itemId } = args;
    return openapi.endpoints.getMcbimItem.select({ projectNo, itemId })(state).data ?? null;
  }
);

export const selectSingleMcBimMmiValue = createSelector(
  [selectHttpGetMcbimItem, selectMmiPath],
  (item, mmiPath) => {
    if (!item || !mmiPath) return null;
    const value = getDeeplyNestedValue(item, mmiPath);
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
      return String(value);
    }
    return null;
  }
);

const formatBool = (v: boolean | undefined | null): string =>
  v === undefined || v === null ? "—" : v ? "Ja" : "Nei";

const formatDateMaybe = (v: unknown): string => {
  if (!v) return "—";
  if (typeof v === "string") return v;
  if (v instanceof Date) return v.toISOString();
  return String(v);
};

const formatPsetValue = (value: string | number | boolean | undefined | null): string => {
  if (value === undefined || value === null) return "—";
  if (typeof value === "boolean") return value ? "Ja" : "Nei";
  if (typeof value === "number") return Number.isFinite(value) ? String(value) : "—";
  if (typeof value === "string") return value.trim().length > 0 ? value : "—";
  return "—";
};

export const selectMcBimItemFlatRows = createSelector(
  selectHttpGetMcbimItem,
  (data: IMcBimItem | null) => {
    if (!data) return [];

    const rows: Array<{ label: string; value: string }> = [];

    // Identification section
    rows.push({ label: "— Identifikasjon —", value: "" });
    rows.push({ label: "MongoId", value: data._id ? String(data._id) : "—" });
    rows.push({ label: "GlobalId", value: data.globalId || "—" });
    rows.push({ label: "InstanceClass", value: data.instanceClass || "—" });
    rows.push({ label: "ExpressId", value: data.expressId ? String(data.expressId) : "—" });

    rows.push({ label: "Type", value: data.type?.name || "—" });

    const buildingStory = data.buildingStory;
    rows.push({ label: "Etasje", value: buildingStory?.name ?? "" });

    rows.push({ label: "Navn", value: data.name || "—" });
    rows.push({ label: "LongName", value: data.longName || "—" });
    rows.push({ label: "Beskrivelse", value: data.description || "—" });

    rows.push({ label: "Filnavn", value: data.fileName || "—" });
    rows.push({ label: "IFC-schema", value: data.fileIfcSchema || "—" });
    rows.push({ label: "Siste versjon", value: formatBool(data.fileIsLatest) });
    rows.push({ label: "Sist endret av", value: data.fileLastModifiedBy || "—" });
    rows.push({ label: "Opplastet", value: formatDateMaybe(data.fileUploadedAt) });

    // Systems section
    rows.push({ label: "— System —", value: "" });

    let systemsValue = "";
    const primarySystem = data.primarySystem;
    if (primarySystem) {
      systemsValue += `Primærsystem: ${primarySystem.name || "—"} (${primarySystem.typeClass || "—"} · ${primarySystem.globalId || "—"}`;
      if (primarySystem.predefinedType !== undefined && primarySystem.predefinedType !== null) {
        systemsValue += ` · ${String(primarySystem.predefinedType)}`;
      }
      systemsValue += ")\n";
    }

    const systems = data.systems || [];
    const uniqueSystems = new Set<string>();
    systems.forEach((s) => {
      let sysStr = `${s.name || "—"} (${s.typeClass || "—"} · ${s.globalId || "—"}`;
      if (s.predefinedType !== undefined && s.predefinedType !== null) {
        sysStr += ` · ${String(s.predefinedType)}`;
      }
      sysStr += ")";
      if (!uniqueSystems.has(sysStr)) {
        uniqueSystems.add(sysStr);
        systemsValue += `${sysStr}\n`;
      }
    });

    rows.push({ label: "Systemer", value: systemsValue.trim() || "—" });

    // Psets section
    rows.push({ label: "— Egenskaper —", value: "" });

    const psets = data.psets || {};
    const psetNames = Object.keys(psets).sort((a, b) => a.localeCompare(b));
    if (psetNames.length === 0) {
      rows.push({ label: "Egenskaper", value: "—" });
    } else {
      psetNames.forEach((psetName) => {
        const props = psets[psetName] || {};
        const keys = Object.keys(props).sort((a, b) => a.localeCompare(b));
        keys.forEach((key) => {
          const value = formatPsetValue(props[key]);
          rows.push({ label: `${psetName}.${key}`, value });
        });
      });
    }

    return rows;
  }
);
